import { PreviewsMap, PreviewVariant } from '../../../../../types/demo-previews';
import { CDN_BASE_URL } from '../const/cdn-base-url';
import { PREVIEWS_CONFIG } from '../config/preview-config';
import { PreviewOptions } from '../types/preview-options';

/**
 * Obtiene los canales disponibles para un stage
 */
export const getAvailableChannels = (stage: string): ('google' | 'meta')[] => {
  return Object.keys(PREVIEWS_CONFIG[stage] || {}) as ('google' | 'meta')[];
};

/**
 * Obtiene las variantes disponibles para un stage + channel
 */
export const getAvailableVariants = (stage: string, channel: string): string[] => {
  return PREVIEWS_CONFIG[stage]?.[channel]?.variants || [];
};

/**
 * Verifica si una combinación stage + channel + variant es válida
 */
export const isValidCombination = (stage: string, channel: string, variant: string): boolean => {
  const config = PREVIEWS_CONFIG[stage]?.[channel];
  return config?.variants.includes(variant) || false;
};

/**
 * Obtiene todas las resoluciones únicas (videos + imágenes) para un stage + channel
 */
const getAllResolutions = (stage: string, channel: string): string[] => {
  const config = PREVIEWS_CONFIG[stage]?.[channel];
  if (!config) return [];

  const combined = [...config.videoResolutions, ...config.imageResolutions];

  return combined.reduce<string[]>((unique, resolution) => {
    if (!unique.includes(resolution)) {
      unique.push(resolution);
    }
    return unique;
  }, []);
};

/**
 * Construye la URL de una preview con sufijos opcionales
 */
const buildPreviewUrl = (
  stage: string,
  channel: string,
  variant: string,
  resolution: string,
  options: PreviewOptions,
  extension: 'mp4' | 'jpg'
): string => {
  const cleanResolution = resolution.replace(/:/g, '-');
  const base = `${stage.toLowerCase()}_${channel}_${variant}_${cleanResolution}`;
  const folder = extension === 'mp4' ? 'previews' : 'thumbnails';

  const hasCTA = options.withCTA;
  const hasSubOrLegal = options.withSubheadline || options.withLegal;

  let suffix = '';

  if (hasCTA && hasSubOrLegal) {
    suffix = '_cta-sub-legal';
  } else if (hasCTA) {
    suffix = '_cta';
  } else if (hasSubOrLegal) {
    suffix = '_sub-legal';
  }

  return `${CDN_BASE_URL}/${folder}/${base}${suffix}.${extension}`;
};

/**
 * Crea un PreviewVariant a partir de los parámetros
 */
const createPreviewVariant = (
  stage: string,
  channel: string,
  variant: string,
  resolution: string,
  options: PreviewOptions
): PreviewVariant => {
  const config = PREVIEWS_CONFIG[stage]?.[channel];

  return {
    video: config?.videoResolutions.includes(resolution)
      ? buildPreviewUrl(stage, channel, variant, resolution, options, 'mp4')
      : undefined,
    image: config?.imageResolutions.includes(resolution)
      ? buildPreviewUrl(stage, channel, variant, resolution, options, 'jpg')
      : undefined,
    metadata: { variant, stage, channel, resolution }
  };
};

/**
 * Obtiene previews para una combinación específica de stage + channel
 */
const getPreviewsForStageAndChannel = (
  stage: string,
  channel: string,
  variant: string | undefined,
  options: PreviewOptions
): PreviewsMap => {
  const config = PREVIEWS_CONFIG[stage]?.[channel];

  if (!config) {
    return {};
  }

  const result: PreviewsMap = {};

  // Obtener todas las resoluciones únicas
  const allResolutions = getAllResolutions(stage, channel);

  // Para cada resolución en la config
  allResolutions.forEach(resolution => {
    // Si hay un variant específico, devolver solo ese
    if (variant) {
      if (config.variants.includes(variant)) {
        result[resolution] = createPreviewVariant(stage, channel, variant, resolution, options);
      }
    } else {
      // Si no hay variant, devolver todas las variantes como array
      result[resolution] = config.variants.map(v => createPreviewVariant(stage, channel, v, resolution, options));
    }
  });

  return result;
};

/**
 * Combina previews de todos los canales para una etapa
 */
const getAllPreviewsForStage = (stage: string, variant: string | undefined, options: PreviewOptions): PreviewsMap => {
  const googlePreviews = getPreviewsForStageAndChannel(stage, 'google', variant, options);
  const metaPreviews = getPreviewsForStageAndChannel(stage, 'meta', variant, options);

  return { ...googlePreviews, ...metaPreviews };
};

export const getDemoPreviews = (formData: any): PreviewsMap => {
  const { stage, adChannel, variant, cta_enabled, subheadline_enabled, legal } = formData;

  const options: PreviewOptions = {
    withCTA: cta_enabled === 'si' || cta_enabled === true,
    withSubheadline: subheadline_enabled === 'si' || subheadline_enabled === true,
    withLegal: legal === 'si' || legal === true
  };

  // Solo stage: mostrar todas las previews de esa etapa (google + meta)
  if (!adChannel) {
    return getAllPreviewsForStage(stage, variant, options);
  }

  // Stage + channel: mostrar previews de ese canal específico
  return getPreviewsForStageAndChannel(stage, adChannel, variant, options);
};
